<script setup lang="ts">
import { useScreen } from '@/composables/useScreen'
import { useAsyncValidator } from '@vueuse/integrations/useAsyncValidator'
import type { Rules } from 'async-validator'
import { useToast } from 'vue-toast-notification'

const { isVisible, isLawyer, id } = defineProps({
  isVisible: {
    type: Boolean,
    required: true,
    default: false
  },
  isLawyer: {
    type: Boolean,
    required: false,
    default: true
  },
  id: {
    type: Number,
    required: true,
    default: 0
  }
})

const { isMobileView } = useScreen()
const { t } = useI18n()
const axios = useAxios()
const toast = useToast()

const formSubmitted = ref(false)
const hoverValue = ref(0)
const isSuccess = ref(false) // indicates if show success window.

const reviewForm = reactive({
  starValue: 4,
  comment: '',
})

const reviewFormRules : Rules = {
  comment: [
    { required: true, message: t('is_required') },
    { type: 'string', min: 20, message: t('minimum_symbols_required', { symbols: 20 })}
  ]
}

const { pass, errorFields, execute } = useAsyncValidator(reviewForm, reviewFormRules, { immediate: false, validateOption: { suppressWarning: true} })

const remainingCommentSymbols = computed (() => { return 500 - (reviewForm.comment.length ?? 0) })

const emit = defineEmits(['close', 'confirm'])

function handlereviewClose() {
  isSuccess.value = false
  formSubmitted.value = false
  reviewForm.comment = ''
  emit('close')
}

async function submitReview() {
  await execute()
  formSubmitted.value = true
  if (pass.value) {
    const context = isLawyer? 'lawyer-reviews' : 'firm-reviews'
    try {
      const response = await axios.post(`internal/${context}/${id}`, {
        comment: reviewForm.comment,
        rating: reviewForm.starValue
      })

      if (response.status === 200) {
        isSuccess.value = true
      } else {
        toast.error(t('system_error'))
        emit('close')
      }
    } catch {
      toast.error(t('system_error'))
      emit('close')
    }
  } 
}
</script>
<template>
  <GTModal :is-open="isVisible" closeIcon class="text-darkBlue" :width="isMobileView? 400 : 671" @close="handlereviewClose()">
    <template #header>
      <div v-if="isSuccess" class="text-30">
        <div class="flex justify-center mt-6 mb-4">
          <IconSuccess class="size-[60px]"></IconSuccess>
        </div>
        {{ t('review_submitted') }}
      </div>
      <div v-else class="mt-1">
        {{ t('leave_review') }}
      </div>
    </template>

    <template #content>
      <div v-if="isSuccess" class="mt-4 mb-8 flex justify-center text-center font-branding">
        {{ $t('review_submitted_description') }}
      </div>

      <div v-else>
        <div class="mt-8 flex justify-center">
          <div v-for="starIndex in 5" class="flex inline-flex">
            <button @click="reviewForm.starValue = starIndex" @mouseover="hoverValue = starIndex" @mouseleave="hoverValue = 0" class="px-1.5">
              <iconRatingStar
                class="size-10"
                :class="{
                  'text-lightBlue': starIndex <= hoverValue || (hoverValue === 0 && starIndex <= reviewForm.starValue),
                  'text-white-500': hoverValue === 0 ? starIndex > reviewForm.starValue : starIndex > hoverValue,
                }"
              />
            </button>
          </div>
        </div>

        <div class="mt-10 mb-8">
          <label>
            {{ t('describe') }}
            <span class="font-normal">
              ({{ t('max_characters', { symbols: 500 })}}. {{ t('remaining_characters', { remainingSymbols: remainingCommentSymbols })}})
            </span>
          </label>
          <textarea
            v-model="reviewForm.comment"
            maxlength="500"
            placeholder="..."
            class="mt-0.5 font-branding border border-white-700 w-full h-[200px] lg:h-[132px] p-2 rounded-lg resize-none focus:border-darkBlue scrollbar scrollbar--round"
            :class="{ '!border-red': errorFields?.comment?.length && formSubmitted }"
          />
          <div v-if="formSubmitted" class="text-red-500">
            <div v-if="errorFields?.comment?.length">
              {{ errorFields.comment[0].message }}
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-center">
        <div v-if="isSuccess">
          <button class="ml-2 w-[250px] h-11 bg-darkBlue hover:bg-lightBlue text-white rounded-full transition-all duration-50 active:scale-95" @click="handlereviewClose()">
            {{ $t('close') }}
          </button>
        </div>
        <div v-else>
          <button class="ml-2 w-[250px] h-11 bg-darkBlue hover:bg-lightBlue text-white rounded-full transition-all duration-50 active:scale-95" @click="submitReview()">
            {{ $t('send') }}
          </button>
        </div>
      </div>
    </template>
  </GTModal>
</template>
